import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { ScrapReason } from '../../../api/backend/types/scrap-reason';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormScrapReasonData } from '../../types/form-scrap-reasons';
import {
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
} from 'rxjs/operators';

type TypeFormControl = {
  quantity: FormControl<number | null>;
  reason: FormControl<ScrapReason | null>;
};

export type TypeFormScrapReason = FormGroup<TypeFormControl>;

@Injectable()
export class FormScrapReasonsService {
  constructor() {}

  protected _scrapReasons = new BehaviorSubject<ScrapReason[]>([]);
  set scrapReasons(data: ScrapReason[]) {
    this._scrapReasons.next(data);
  }
  scrapReasons$ = this._scrapReasons.asObservable();

  searchReasonControl = new FormControl<string>('');

  protected formArray = new BehaviorSubject<TypeFormScrapReason[]>([
    this.createFormGroup(),
  ]);

  formArray$ = this.formArray.asObservable();

  protected _quantity = 0;
  set quantity(data: number) {
    this._quantity = data;
  }
  get quantity(): number {
    return this._quantity;
  }

  reasonsFiltered$ = combineLatest([
    this.scrapReasons$,
    this.searchReasonControl.valueChanges.pipe(startWith('')),
  ]).pipe(
    map(([reasons, search]) => {
      if (!search) {
        return reasons;
      }
      return reasons.filter(
        (m) =>
          m.title.toLowerCase().includes(search.toLowerCase()) ||
          m.code?.toLowerCase().includes(search.toLowerCase())
      );
    })
  );

  reasonsAlreadySelected$ = this.formArray$.pipe(
    switchMap((groups) =>
      combineLatest(
        groups.map((group) =>
          group
            .get('reason')
            .valueChanges.pipe(startWith(group.get('reason').value))
        )
      )
    ),
    map((reasons) =>
      reasons.filter((r): r is ScrapReason => !!r).map((r) => r.id)
    ),
    distinctUntilChanged()
  );

  addReasonValidation$ = combineLatest([
    this.formArray$.pipe(
      map((res) => res.length),
      distinctUntilChanged()
    ),
    this.scrapReasons$.pipe(
      map((res) => res.length),
      distinctUntilChanged()
    ),
  ]).pipe(map(([form, reasons]) => reasons > form));

  protected createFormGroup(): TypeFormScrapReason {
    return new FormGroup<TypeFormControl>({
      quantity: new FormControl<number | null>(null, Validators.required),
      reason: new FormControl<ScrapReason | null>(null, Validators.required),
    });
  }

  isQuantityValid(): boolean {
    let sum = 0;
    this.formArray.getValue().forEach((group) => {
      sum += group.get('quantity')?.value ?? 0;
    });
    return sum === this._quantity;
  }

  isFormValid(): boolean {
    return (
      this.formArray.getValue().every((form) => form.valid) &&
      this.isQuantityValid()
    );
  }

  getData(): FormScrapReasonData[] {
    return this.formArray.getValue().map((group) => {
      const reason = group.get('reason')?.value;
      return {
        quantity: group.get('quantity')?.value,
        reasonId: reason?.id,
      };
    });
  }

  resetForm(): void {
    this.formArray.next([this.createFormGroup()]);
  }

  addNewReason(): void {
    this.formArray.next([...this.formArray.getValue(), this.createFormGroup()]);
  }

  removeReason(index: number): void {
    this.formArray.next(
      this.formArray.getValue().filter((_, i) => i !== index)
    );
  }
}
